import { useEffect, useState } from "react";
import { ArrowUpRight, MessageCircle, Calendar } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import { buildWhatsAppUrl } from "@/lib/constants";
import { trackWaClick } from "@/lib/track";

// Upcoming missions / events — real agenda. Drives WhatsApp conversion.
const UPCOMING = [
  {
    month: { es: "AGO", en: "AUG" },
    days: "19·20",
    year: "2026",
    name: "IBTM Américas",
    city: { es: "México", en: "Mexico" },
    role: { es: "Misión · Representación", en: "Mission · Representation" },
  },
  {
    month: { es: "SEP", en: "SEP" },
    days: "—",
    year: "2026",
    name: "Forever Wedding Summit",
    city: { es: "Medellín, Colombia", en: "Medellín, Colombia" },
    role: { es: "Operación · Misión de mercado", en: "Operation · Market mission" },
  },
  {
    month: { es: "ENE", en: "JAN" },
    days: "20·24",
    year: "2027",
    name: "FITUR",
    city: { es: "Madrid, España", en: "Madrid, Spain" },
    role: { es: "Representación · Misión", en: "Representation · Mission" },
  },
  {
    month: { es: "FEB", en: "FEB" },
    days: "—",
    year: "2027",
    name: "Vitrina Turística ANATO",
    city: { es: "Bogotá, Colombia", en: "Bogotá, Colombia" },
    role: { es: "Representación", en: "Representation" },
  },
];

export const Hero = () => {
  const { locale } = useSite();
  const { t } = useT(locale);

  // Word rotator — focused on B2B audiences
  const words = locale === "es"
    ? ["EMPRESAS", "GREMIOS", "INSTITUCIONES", "TERRITORIOS", "MERCADOS"]
    : ["COMPANIES", "GUILDS", "INSTITUTIONS", "TERRITORIES", "MARKETS"];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setIdx((v) => (v + 1) % words.length), 1900);
    return () => clearInterval(i);
  }, [words.length]);

  return (
    <section
      id="top"
      className="relative w-full overflow-hidden ix-grid-bg"
      data-testid="hero-section"
      style={{ backgroundColor: "var(--ix-night)", color: "var(--ix-cream)" }}
    >
      {/* Floating diamond shapes — Incubatour DNA (2 large only) */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="ix-float-1 absolute"
          style={{
            top: "12%", right: "6%",
            width: "240px", height: "320px",
            background: "linear-gradient(135deg, #FF1F8F 0%, #FCB424 100%)",
            clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
            opacity: 0.78,
          }}
        />
        <div
          className="ix-float-2 absolute"
          style={{
            top: "36%", right: "22%",
            width: "170px", height: "230px",
            background: "linear-gradient(135deg, #FCB424 0%, #8AE52E 100%)",
            clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
            opacity: 0.42,
          }}
        />
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 75% 35%, rgba(252,180,36,0.13) 0%, transparent 55%), radial-gradient(ellipse at 18% 75%, rgba(255,31,143,0.06) 0%, transparent 60%)",
          }}
        />
      </div>

      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 pt-[110px] md:pt-[140px] pb-12 md:pb-16 relative">
        {/* Top metadata bar — single, clean line */}
        <div className="ix-rise flex flex-wrap items-center gap-3 md:gap-5 mb-12 md:mb-14" style={{ animationDelay: "0.05s" }}>
          <span
            aria-hidden
            style={{
              width: 14, height: 18,
              background: "linear-gradient(135deg, #FF1F8F, #FCB424, #8AE52E)",
              clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
            }}
          />
          <span className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/70">
            {t("hero.eyebrow")}
          </span>
          <span className="hidden md:inline w-8 h-px bg-[var(--ix-cream)]/25" />
          {/* DMC certification badge — credibility signal for institutional/corporate visitors */}
          <span
            className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 ix-mono text-[0.62rem] uppercase tracking-[0.18em]"
            style={{
              background: "linear-gradient(135deg, rgba(255,31,143,0.10) 0%, rgba(252,180,36,0.10) 100%)",
              border: "1px solid rgba(252, 244, 224, 0.18)",
            }}
            title={locale === "es" ? "Operadora certificada de destino · Registro Nacional de Turismo de Colombia 278668" : "Destination Management Company · Colombia National Tourism Registry 278668"}
            data-testid="hero-dmc-badge"
          >
            <span className="ix-grad-text font-semibold">◆</span>
            <span className="text-[var(--ix-cream)]/95 font-semibold">DMC</span>
            <span className="text-[var(--ix-cream)]/45">·</span>
            <span className="text-[var(--ix-cream)]/85">RNT 278668</span>
          </span>
        </div>

        {/* Massive headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative z-10">
          <div className="lg:col-span-9">
            <h1 className="ix-display text-[var(--ix-cream)]">
              <span className="block ix-rise ix-headline-xl" style={{ animationDelay: "0.15s" }}>
                {t("hero.title_a").toUpperCase()}
              </span>

              {/* Rotating word strip */}
              <span
                className="ix-rise ix-headline-xl relative block overflow-hidden mt-1"
                style={{ animationDelay: "0.3s" }}
                data-testid="hero-rotator"
              >
                {/* Reserve height with invisible widest word */}
                <span className="invisible block">{words.reduce((a, b) => (a.length > b.length ? a : b))}</span>
                {words.map((w, i) => (
                  <span
                    key={w}
                    className="block absolute inset-0 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    style={{
                      transform: i === idx ? "translateY(0)" : "translateY(105%)",
                      opacity: i === idx ? 1 : 0,
                    }}
                  >
                    <span className="ix-grad-text">{w}</span>
                  </span>
                ))}
              </span>

              <span className="block ix-rise ix-headline-xl mt-1" style={{ animationDelay: "0.45s" }}>
                {locale === "es" ? "CON " : "WITH "}
                <span className="ix-grad-text" style={{ fontWeight: 700 }}>{locale === "es" ? "PROPÓSITO." : "PURPOSE."}</span>
              </span>
            </h1>

            <p
              className="ix-rise mt-10 max-w-2xl text-[1rem] md:text-[1.08rem] leading-relaxed text-[var(--ix-cream)]/75"
              style={{ animationDelay: "0.6s" }}
              data-testid="hero-sub"
            >
              {t("hero.sub")}
            </p>

            <div className="ix-rise mt-9 flex flex-col sm:flex-row gap-3" style={{ animationDelay: "0.75s" }}>
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWaClick("hero_main")}
                className="ix-btn ix-btn-grad"
                data-testid="hero-whatsapp-cta"
              >
                <MessageCircle size={15} strokeWidth={1.8} />
                {t("hero.cta_primary")}
                <ArrowUpRight size={14} strokeWidth={1.8} />
              </a>
              <a href="#lineas" className="ix-btn ix-btn-ghost" data-testid="hero-explore-cta">
                {t("hero.cta_secondary")}
              </a>
            </div>
          </div>

          {/* Right: Próxima agenda — drives action */}
          <div className="ix-rise lg:col-span-3 relative" style={{ animationDelay: "0.5s" }}>
            <div
              className="relative border border-[var(--ix-cream)]/15 overflow-hidden"
              style={{ background: "rgba(11,14,30,0.65)" }}
              data-testid="hero-upcoming"
            >
              {/* gradient corner accent */}
              <div
                aria-hidden
                className="absolute -top-6 -right-6 w-24 h-32 opacity-70 pointer-events-none"
                style={{
                  background: "linear-gradient(135deg, #FF1F8F 0%, #FCB424 60%, #8AE52E 100%)",
                  clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                }}
              />

              <div className="px-4 pt-5 pb-3 md:px-6 md:pt-6 md:pb-4 border-b border-[var(--ix-cream)]/10 flex items-start gap-2.5 md:gap-3 relative">
                <Calendar size={18} strokeWidth={1.6} className="mt-[5px] text-[var(--ix-cream)]/75 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="ix-mono uppercase tracking-[0.2em] text-[var(--ix-cream)]/70" style={{ fontSize: "clamp(0.62rem, 0.8vw, 0.75rem)" }}>
                    ▸ {t("hero.upcoming_eyebrow")}
                  </div>
                  <div className="ix-display leading-tight mt-1 text-[var(--ix-cream)]" style={{ fontWeight: 700, fontSize: "clamp(1.15rem, 1.7vw, 1.65rem)" }}>
                    {t("hero.upcoming_title")}
                  </div>
                  <div className="text-[var(--ix-cream)]/70 mt-2 leading-relaxed" style={{ fontWeight: 400, fontSize: "clamp(0.78rem, 0.95vw, 0.95rem)" }}>
                    {t("hero.upcoming_hint")}
                  </div>
                </div>
              </div>

              <ul className="divide-y divide-[var(--ix-cream)]/10">
                {UPCOMING.map((ev, i) => {
                  const month = ev.month[locale] || ev.month.es;
                  const city = ev.city[locale] || ev.city.es;
                  const role = ev.role[locale] || ev.role.es;
                  const dateStr = ev.days !== "—" ? `${month} ${ev.days} · ${ev.year}` : `${month} · ${ev.year}`;
                  const msg = locale === "es"
                    ? `Hola, quiero saber más sobre ${ev.name} (${dateStr}) en ${city}. ¿Cómo puedo participar?`
                    : `Hi, I'd like to know more about ${ev.name} (${dateStr}) in ${city}. How can I take part?`;
                  return (
                    <li key={ev.name} data-testid={`upcoming-event-${i}`}>
                      <a
                        href={buildWhatsAppUrl(msg)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackWaClick(`hero_event_${ev.name.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 30)}`, ev.name)}
                        className="group block px-4 py-3.5 md:px-6 md:py-5 hover:bg-[var(--ix-cream)]/[0.05] transition-colors relative"
                      >
                        <div className="flex items-start gap-3 md:gap-4">
                          {/* Date block — fluid sizing */}
                          <div className="flex-shrink-0 text-center" style={{ minWidth: "clamp(46px, 4.5vw, 60px)" }}>
                            <div className="ix-mono uppercase tracking-[0.14em] ix-grad-text font-bold leading-tight" style={{ fontSize: "clamp(0.65rem, 0.85vw, 0.8rem)" }}>
                              {month}
                            </div>
                            <div className="ix-display leading-tight text-[var(--ix-cream)] mt-1" style={{ fontWeight: 700, fontSize: "clamp(1.1rem, 1.5vw, 1.5rem)" }}>
                              {ev.days !== "—" ? ev.days : ev.year}
                            </div>
                            {ev.days !== "—" && (
                              <div className="ix-mono tracking-[0.1em] text-[var(--ix-cream)]/60 mt-[2px]" style={{ fontSize: "clamp(0.6rem, 0.75vw, 0.72rem)" }}>
                                {ev.year}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="ix-display leading-tight text-[var(--ix-cream)]" style={{ fontWeight: 700, fontSize: "clamp(1.05rem, 1.35vw, 1.35rem)" }}>
                              {ev.name}
                            </div>
                            <div className="text-[var(--ix-cream)]/80 mt-1.5" style={{ fontWeight: 400, fontSize: "clamp(0.8rem, 0.95vw, 0.95rem)" }}>
                              {city}
                            </div>
                            <div className="ix-mono uppercase tracking-[0.14em] text-[var(--ix-cream)]/60 mt-1.5" style={{ fontWeight: 500, fontSize: "clamp(0.62rem, 0.78vw, 0.72rem)" }}>
                              {role}
                            </div>
                            <div className="ix-mono uppercase tracking-[0.16em] text-[var(--ix-cream)]/95 mt-2.5 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ fontSize: "clamp(0.62rem, 0.78vw, 0.72rem)" }}>
                              <span className="ix-grad-text font-semibold">▸</span>
                              {locale === "es" ? "Preguntar por este evento" : "Ask about this event"}
                            </div>
                          </div>
                          <ArrowUpRight
                            strokeWidth={1.7}
                            className="flex-shrink-0 mt-1 text-[var(--ix-cream)]/55 group-hover:text-[var(--ix-cream)] group-hover:translate-x-[2px] group-hover:-translate-y-[2px] transition-all"
                            style={{ width: "clamp(16px, 1.2vw, 20px)", height: "clamp(16px, 1.2vw, 20px)" }}
                          />
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>

              <div className="px-4 py-3 md:px-6 md:py-4 border-t border-[var(--ix-cream)]/10 text-[var(--ix-cream)]/65 leading-relaxed" style={{ fontWeight: 400, fontSize: "clamp(0.78rem, 0.9vw, 0.9rem)" }}>
                {t("hero.upcoming_footnote")}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats strip */}
        <div className="ix-rise mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 border-t border-[var(--ix-cream)]/15 pt-7" style={{ animationDelay: "0.95s" }}>
          {[
            { v: "+25", l: locale === "es" ? "años en el sector" : "years in the sector" },
            { v: "+100", l: locale === "es" ? "productos diseñados" : "products designed" },
            { v: "3", l: locale === "es" ? "territorios activos" : "active territories" },
            { v: "RNT", l: "278668" },
          ].map((s, i) => (
            <div key={`${s.v}-${s.l}`} data-testid={`hero-stat-${i}`}>
              <div className="ix-display text-[2rem] md:text-[2.4rem] leading-none ix-grad-text">{s.v}</div>
              <div className="ix-mono mt-2 text-[0.64rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/55">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Marquee — calmer */}
      <div className="border-y border-[var(--ix-cream)]/8 py-4 overflow-hidden relative">
        <div className="ix-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6 ix-mono text-[0.74rem] uppercase tracking-[0.2em]">
              <span className="text-[var(--ix-cream)]/75">{locale === "es" ? "Misiones empresariales" : "Commercial missions"}</span>
              <span className="text-[var(--ix-cream)]/30">·</span>
              <span className="text-[var(--ix-cream)]/75">{locale === "es" ? "Misiones académicas" : "Academic missions"}</span>
              <span className="text-[var(--ix-cream)]/30">·</span>
              <span className="text-[var(--ix-cream)]/75">{locale === "es" ? "Misiones tecnológicas" : "Technical missions"}</span>
              <span className="text-[var(--ix-cream)]/30">·</span>
              <span className="text-[var(--ix-cream)]/75">{locale === "es" ? "Ferias y representaciones" : "Fairs & destination representation"}</span>
              <span className="text-[var(--ix-cream)]/30">·</span>
              <span className="ix-grad-text font-semibold">Iberoamérica</span>
              <span className="text-[var(--ix-cream)]/30">·</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
